import { TOURS, type ProfileType, type TourStep } from 'shared';

/**
 * Отметка «обучение пройдено» живёт в браузере, а не на сервере.
 *
 * Плата за это — тур покажется ещё раз на новом устройстве. Плата за
 * обратное — колонка в базе, миграция и роут ради одного булева значения,
 * причём повторное обучение на незнакомом экране скорее полезно, чем нет.
 * Появится потребность возить отметку между устройствами — переносится
 * в профиль без изменения остального кода.
 */
const SEEN_KEY = 'gruzbot.tour.seen';

/**
 * Версия содержания тура. Подняли — тур покажется заново всем, кто видел
 * старую версию. Нужна, когда шаги меняются по существу, а не по опечатке.
 */
export const TOUR_VERSION = 1;

/**
 * Хранилище может быть недоступно: режим инкогнито, запрет данных сайта,
 * webview с урезанными правами. Обучение — не то, ради чего стоит падать,
 * поэтому любая ошибка читается как «не видел».
 */
function read(): number {
  try {
    if (typeof localStorage === 'undefined') return 0;
    return Number(localStorage.getItem(SEEN_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function tourSeen(): boolean {
  return read() >= TOUR_VERSION;
}

export function markTourSeen(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(SEEN_KEY, String(TOUR_VERSION));
  } catch {
    /* Не смогли запомнить — тур повторится. Это лучше, чем белый экран. */
  }
}

export function resetTour(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(SEEN_KEY);
  } catch {
    /* см. markTourSeen */
  }
}

export function tourFor(profile: ProfileType): TourStep[] {
  return TOURS[profile] ?? [];
}

/**
 * Следующий показуемый шаг в направлении `dir`, начиная с `from`.
 *
 * Шаги с недоступным якорем пропускаются: разделы скрыты флагами функций
 * и правами, и у конкретного человека половины кнопок может не быть.
 * Шаг, который сам открывает раздел (`screen`), считается доступным
 * заранее — его якорь появится только после перехода.
 *
 * Возвращает `null`, когда показывать в эту сторону больше нечего:
 * для «вперёд» это конец тура, для «назад» — что уходить некуда.
 */
export function stepIndex(
  steps: TourStep[],
  from: number,
  dir: 1 | -1,
  isAvailable: (anchor: string) => boolean,
): number | null {
  for (let i = from; i >= 0 && i < steps.length; i += dir) {
    const step = steps[i]!;
    if (step.screen || isAvailable(step.anchor)) return i;
  }
  return null;
}

/** Есть ли вообще что показывать этому человеку. */
export function hasVisibleSteps(
  steps: TourStep[],
  isAvailable: (anchor: string) => boolean,
): boolean {
  return stepIndex(steps, 0, 1, isAvailable) !== null;
}
